import { useState } from 'react';
import { useCurrentUser } from '../../../shared/features/session/components/CurrentUserProvider';
import { formatMoney } from '../../../shared/policies/format-money';
import { useToast } from '../../../shared/hooks/use-toast';
import { buildExpenseColumns } from '../components/build-expense-columns';
import { createExpense } from '../expenses.api';
import { toCreateExpenseInput, type ExpenseFormValues } from '../policies/expense-form';
import { useExpenseCategories } from './use-expense-categories';
import { useExpenseList } from './use-expense-list';

/** ExpensesPage's own state: the list, the categories, and the record drawer with its inline error (a form failure stays inline, not a toast). */
export function useExpensesPage() {
  const { state, list, isRefreshing, reload } = useExpenseList();
  const categories = useExpenseCategories();
  const { state: currentUser } = useCurrentUser();
  const toast = useToast();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  /** `onbehalf.capture` shows the button; the server stays the authority, so this is UX only. */
  const canRecord = currentUser.status === 'authenticated' && currentUser.user.permissions.includes('onbehalf.capture');
  const columns = buildExpenseColumns({ list, categories });

  /** Returns whether it succeeded, so the drawer knows to close itself. */
  async function record(values: ExpenseFormValues): Promise<boolean> {
    setError(null);
    setSubmitting(true);
    try {
      const capturesFuel = categories.find((category) => category.code === values.categoryCode)?.capturesFuelDetails ?? false;
      const created = await createExpense(toCreateExpenseInput(values, capturesFuel));
      await reload();
      toast.success(`Expense recorded: ${created.category.name} ${formatMoney(created.amount)} for ${created.driverName}`);
      return true;
    } catch {
      setError('Could not record the expense — check the fields and try again.');
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  function openDrawer() {
    setError(null);
    setDrawerOpen(true);
  }

  return {
    state,
    list,
    isRefreshing,
    categories,
    columns,
    canRecord,
    drawerOpen,
    openDrawer,
    closeDrawer: () => setDrawerOpen(false),
    record,
    error,
    submitting,
  };
}
